import React, {useContext, useEffect, useState} from "react";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {getAuthenticationApi, getMedicalStaffApi, getPatientApi, getUserApi} from "../api/apiInstances";
import {Principal} from "../types/Principal";
import {tokenService} from "../services/tokenService";
import {isAnonymousPath} from "../constants/anonymousPaths";
import logger from "../utils/logger";
import {message} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {useTrackEvent} from "../hooks/useTrackEvent";

interface AuthenticationProps {
    children?: any;
}

const Authentication = (props: AuthenticationProps) => {

    const {setPrincipal} = useContext(DentobookApplicationContext);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
    const userApi = getUserApi();
    const medicalApi = getMedicalStaffApi();
    const patientApi = getPatientApi();
    const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
    const [onboardingBlock, setOnboardingBlock] = useState<'none' | 'email' | 'terms'>('none');
    const [resendLoading, setResendLoading] = useState(false);
    const [acceptTermsLoading, setAcceptTermsLoading] = useState(false);
    const intl = useIntl();
    const track = useTrackEvent();

    // Track when blocking onboarding screens become visible. These are distinct
    // funnel signals from the Login.tsx modal path — they fire when a user is
    // authenticated but blocked by an incomplete onboarding step.
    useEffect(() => {
        if (onboardingBlock === 'email') {
            track('email_gate_viewed');
        }
        if (onboardingBlock === 'terms') {
            track('terms_gate_viewed');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboardingBlock]);

    // Shared helper — clear tokens and redirect to login, preserving redirect URL.
    const _doLogout = () => {
        tokenService.clearTokens();
        const currentPath = window.location.pathname + window.location.search;
        tokenService.setRedirectUrl(currentPath);
        window.location.href = '/login';
    };

    const handleResendVerification = async () => {
        setResendLoading(true);
        try {
            await getAuthenticationApi().resendUserVerificationEmail();
            message.success(intl.formatMessage({id: 'email_not_verified_resend_success'}));
        } catch {
            message.error(intl.formatMessage({id: 'unexpected_error'}));
        } finally {
            setResendLoading(false);
        }
    };

    const handleAcceptTerms = async () => {
        setAcceptTermsLoading(true);
        try {
            await getAuthenticationApi().acceptTerms({accepted: true});
            track('terms_gate_accepted');
            // Signal App.tsx to fire dashboard_viewed_first_time after the reload.
            // sessionStorage persists through window.location.reload() within the tab.
            sessionStorage.setItem('dentobook_terms_just_accepted', '1');
            window.location.reload();
        } catch {
            message.error(intl.formatMessage({id: 'unexpected_error'}));
            setAcceptTermsLoading(false);
        }
    };

    useEffect(() => {
        const pathIsAnonymous = isAnonymousPath(window.location.pathname);
        setIsAnonymous(pathIsAnonymous);

        if (pathIsAnonymous) {
            setPrincipal({
                user: undefined,
                medicalPerson: undefined,
                patient: undefined,
                features: []
            });

            return;
        }

        // Check if user has JWT token
        if (!tokenService.isAuthenticated()) {
            // No token, redirect to login
            // Don't save redirect URL for anonymous paths like reset-password
            window.location.href = '/login';
            return;
        }

        // User is authenticated, fetch principal data
        userApi.fetchPrincipal()
            .then((userResponse) => {
                // Prefer membershipRole from JWT (reflects OrganizationMembership.role for
                // the selected org). Falls back to userResponse.data.role for old tokens
                // that don't carry the claim yet (backward compat).
                const tokenPayload = tokenService.decodeToken();
                const effectiveRole = tokenPayload?.membershipRole ?? userResponse.data.role;

                const currentPrincipal: Principal = {
                    user: { ...userResponse.data, role: effectiveRole as any },
                    medicalPerson: undefined,
                    patient: undefined,
                    features: userResponse.data.features ?? []
                }

                if (userResponse.data.medicalStaffId) {
                    medicalApi.fetchMedicalStaffById(userResponse.data.medicalStaffId)
                        .then((medicalResponse) =>
                            currentPrincipal.medicalPerson = medicalResponse.data)
                        .catch((error) => {
                            logger.error("Failed to fetch medical staff:", error);
                            // Medical staff not found (possibly deleted), set to undefined
                            currentPrincipal.medicalPerson = undefined;
                        });
                } else if (userResponse.data.patientId) {
                    patientApi.fetchPatientById(userResponse.data.patientId)
                        .then((patientResponse) =>
                            currentPrincipal.patient = patientResponse.data)
                        .catch((error) => {
                            logger.error("Failed to fetch patient:", error);
                            // Patient not found (possibly deleted), set to undefined
                            currentPrincipal.patient = undefined;
                        });
                }

                setPrincipal(currentPrincipal);
                setIsAuthenticated(true);
            })
            .catch((error) => {
                const is403 = error?.response?.status === 403;

                // Check for onboarding gate errors before attempting org-switch recovery.
                // These errors mean the user's account is incomplete, not that their org
                // membership is stale — org-switch would not help here.
                const errorCode = error?.response?.data?.detail?.error_code;
                if (errorCode === 'EMAIL_NOT_VERIFIED') {
                    setOnboardingBlock('email');
                    return;
                }
                if (errorCode === 'TERMS_NOT_ACCEPTED') {
                    setOnboardingBlock('terms');
                    return;
                }

                // If the axios interceptor already handled this 403 via org-switch recovery and
                // set a session event (before scheduling window.location.assign('/')), do nothing —
                // the page reload will re-initialize everything in the new org context.
                if (sessionStorage.getItem('dentobook_session_event')) return;

                // Recovery path: 403 may mean the user's selected org membership was removed
                // while they still have other ACTIVE memberships elsewhere.
                // Attempt ONE auto-switch before giving up — prevents the lockout loop.
                if (is403 && tokenService.isAuthenticated()) {
                    logger.warn("fetchPrincipal returned 403 — attempting org-switch recovery");
                    const authApi = getAuthenticationApi();
                    authApi.getUserOrganizations()
                        .then((orgsResponse) => {
                            const activeOrgs = (orgsResponse.data || []).filter(
                                (org) => org.status === 'active'
                            );
                            if (activeOrgs.length === 0) {
                                // No active orgs at all — user is truly unauthorized
                                logger.warn("No ACTIVE orgs found — logging out");
                                _doLogout();
                                return;
                            }
                            // Switch to the first available ACTIVE org and retry principal fetch
                            authApi.selectOrganization({ organizationId: activeOrgs[0].organizationId })
                                .then((tokenResponse) => {
                                    tokenService.setTokens(
                                        tokenResponse.data.access_token,
                                        tokenResponse.data.refresh_token
                                    );
                                    return userApi.fetchPrincipal();
                                })
                                .then((userResponse) => {
                                    const tokenPayload = tokenService.decodeToken();
                                    const effectiveRole = tokenPayload?.membershipRole ?? userResponse.data.role;
                                    const recoveredPrincipal: Principal = {
                                        user: { ...userResponse.data, role: effectiveRole as any },
                                        medicalPerson: undefined,
                                        patient: undefined,
                                        features: userResponse.data.features ?? []
                                    };
                                    setPrincipal(recoveredPrincipal);
                                    setIsAuthenticated(true);
                                    logger.info("Org-switch recovery succeeded");
                                })
                                .catch((retryError) => {
                                    logger.error("Org-switch recovery failed:", retryError);
                                    _doLogout();
                                });
                        })
                        .catch((orgsError) => {
                            logger.error("getUserOrganizations failed during recovery:", orgsError);
                            _doLogout();
                        });
                } else {
                    // Non-403 error (401, network error, etc.) — clear and redirect immediately
                    logger.error("Failed to fetch principal:", error);
                    _doLogout();
                }
            });
    }, []);

    if (isAnonymous) {
        return (
            <>{props.children}</>
        );
    }

    // Email not verified — blocking screen until user confirms their email
    if (onboardingBlock === 'email') {
        return (
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '24px', textAlign: 'center'}}>
                <img
                    alt="logo"
                    className="dentobook-logo"
                    src={require('../assets/logo.png')}
                    style={{width: '280px', height: 'auto', marginBottom: '32px'}}
                />
                <div style={{maxWidth: 480, background: '#fff', borderRadius: '24px', padding: '40px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'}}>
                    <h2 style={{fontSize: '22px', fontWeight: 600, marginBottom: '12px'}}>
                        <FormattedMessage id="email_not_verified_title"/>
                    </h2>
                    <p style={{color: '#666', marginBottom: '28px', lineHeight: 1.6}}>
                        <FormattedMessage id="email_not_verified_subtitle"/>
                    </p>
                    <button
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        style={{width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '12px', border: '1px solid #1890ff', background: '#1890ff', color: '#fff', fontSize: '15px', cursor: resendLoading ? 'not-allowed' : 'pointer', opacity: resendLoading ? 0.7 : 1}}
                    >
                        {resendLoading
                            ? '...'
                            : <FormattedMessage id="email_not_verified_resend"/>
                        }
                    </button>
                    <button
                        onClick={_doLogout}
                        style={{width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d9d9d9', background: '#fff', color: '#555', fontSize: '15px', cursor: 'pointer'}}
                    >
                        <FormattedMessage id="email_not_verified_logout"/>
                    </button>
                </div>
            </div>
        );
    }

    // Terms not accepted — blocking modal until user accepts
    if (onboardingBlock === 'terms') {
        return (
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '24px', textAlign: 'center'}}>
                <img
                    alt="logo"
                    className="dentobook-logo"
                    src={require('../assets/logo.png')}
                    style={{width: '280px', height: 'auto', marginBottom: '32px'}}
                />
                <div style={{maxWidth: 480, background: '#fff', borderRadius: '24px', padding: '40px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'}}>
                    <h2 style={{fontSize: '22px', fontWeight: 600, marginBottom: '12px'}}>
                        <FormattedMessage id="terms_not_accepted_title"/>
                    </h2>
                    <p style={{color: '#666', marginBottom: '28px', lineHeight: 1.6}}>
                        <FormattedMessage id="terms_not_accepted_subtitle"/>
                    </p>
                    <p style={{marginBottom: '28px'}}>
                        I have read and agreed with the{' '}
                        <a href="https://dentobook.com/en/terms-and-conditions/" target="_blank" rel="noopener noreferrer" style={{color: '#1890ff'}}>
                            Terms and Conditions
                        </a>
                        {' '}and the{' '}
                        <a href="https://dentobook.com/en/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{color: '#1890ff'}}>
                            Privacy Policy
                        </a>
                    </p>
                    <button
                        onClick={handleAcceptTerms}
                        disabled={acceptTermsLoading}
                        style={{width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '12px', border: '1px solid #1890ff', background: '#1890ff', color: '#fff', fontSize: '15px', cursor: acceptTermsLoading ? 'not-allowed' : 'pointer', opacity: acceptTermsLoading ? 0.7 : 1}}
                    >
                        {acceptTermsLoading ? '...' : 'Accept and continue'}
                    </button>
                    <button
                        onClick={_doLogout}
                        style={{width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d9d9d9', background: '#fff', color: '#555', fontSize: '15px', cursor: 'pointer'}}
                    >
                        <FormattedMessage id="email_not_verified_logout"/>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {isAuthenticated &&
                <>{props.children}</>
            }
        </>
    );
}

export default Authentication;
